var _a;
import { __assign, __extends, __rest } from "tslib";
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { Panel } from '../Panel';
import { TabTrap } from './components/TabTrap';
import { ModalContext } from './components/ModalContext';
import { withFocus, classNames } from '../utils';
import tokens from '@metro-ui/tokens/css/custom-properties.module.css';
import styles from './Modal.module.css';
import { useRef } from 'react';
var PanelWithFocus = withFocus(Panel);
var ModalPanel = /** @class */ (function (_super) {
    __extends(ModalPanel, _super);
    function ModalPanel() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.handleEsc = function (e) {
            if ((e.key === 'Escape' || e.key === 'Esc') && _this.props.onDismiss) {
                _this.props.onDismiss(e);
            }
        };
        return _this;
    }
    ModalPanel.prototype.componentDidMount = function () {
        window.addEventListener('keyup', this.handleEsc);
    };
    ModalPanel.prototype.componentWillUnmount = function () {
        window.removeEventListener('keyup', this.handleEsc);
    };
    ModalPanel.prototype.render = function () {
        var _a = this.props, onDismiss = _a.onDismiss, modalRef = _a.modalRef, props = __rest(_a, ["onDismiss", "modalRef"]);
        return (React.createElement(ModalContext.Provider, { value: { onDismiss: onDismiss } },
            React.createElement(PanelWithFocus, __assign({}, props, { overflowHidden: true, ref: modalRef }))));
    };
    return ModalPanel;
}(React.Component));
var getTransitionClassNames = function (isFullScreen, isRightAligned) {
    if (!isFullScreen && isRightAligned) {
        return {
            enter: styles.modalPanelRightAlignedEnter,
            enterActive: styles.modalPanelRightAlignedEnterActive,
            exit: styles.modalPanelRightAlignedExit,
            exitActive: styles.modalPanelRightAlignedExitActive,
        };
    }
    return {
        enter: styles.modalPanelEnter,
        enterActive: styles.modalPanelEnterActive,
        exit: styles.modalPanelExit,
        exitActive: styles.modalPanelExitActive,
    };
};
export var Modal = function (_a) {
    var _b, _c;
    var isOpen = _a.isOpen, _d = _a.closeOnOutsideClick, closeOnOutsideClick = _d === void 0 ? false : _d, initialFocus = _a.focusOn, returnFocusTo = _a.returnFocusTo, onDismiss = _a.onDismiss, _e = _a.className, className = _e === void 0 ? '' : _e, _f = _a.overlayVariant, overlayVariant = _f === void 0 ? 'default' : _f, _g = _a.label, label = _g === void 0 ? 'Modal' : _g, children = _a.children, _h = _a.zIndex, zIndex = _h === void 0 ? 2 : _h, _j = _a.isFullScreen, isFullScreen = _j === void 0 ? false : _j, _k = _a.isRightAligned, isRightAligned = _k === void 0 ? false : _k, minWidth = _a.minWidth, htmlAttributes = __rest(_a, ["isOpen", "closeOnOutsideClick", "focusOn", "returnFocusTo", "onDismiss", "className", "overlayVariant", "label", "children", "zIndex", "isFullScreen", "isRightAligned", "minWidth"]);
    var modalRef = useRef(null);
    var zIndexStyle = zIndex ? { zIndex: zIndex } : {};
    var transitionClassNames = getTransitionClassNames(isFullScreen, isRightAligned);
    var minWidthStyle = minWidth
        ? {
            '--mds-comp-modal-minWidth': typeof minWidth === 'number' ? "".concat(minWidth, "px") : minWidth,
        }
        : {};
    var onOverlayClick = React.useCallback(function (e) {
        if (closeOnOutsideClick && onDismiss && e.target === e.currentTarget) {
            onDismiss(e);
        }
    }, [closeOnOutsideClick, onDismiss]);
    var onOverlayKeyDown = React.useCallback(function (e) {
        if (closeOnOutsideClick && onDismiss && e.key === 'Enter') {
            onDismiss(e);
        }
    }, [closeOnOutsideClick, onDismiss]);
    var modalClassNames = classNames((_b = {},
        _b[tokens.mdsComp] = true,
        _b[styles.mdsCompModal] = true,
        _b[styles.isFullScreen] = isFullScreen,
        _b[styles.isRightAligned] = isRightAligned,
        _b[className] = true,
        _b));
    var modalOverlayClassNames = classNames((_c = {},
        _c[tokens.mdsComp] = true,
        _c[styles.modalOverlay] = true,
        _c[styles.modalOverlayDark] = overlayVariant === 'dark',
        _c));
    var ModalContent = (React.createElement(TabTrap, { innerTrapRef: modalRef },
        React.createElement(ModalPanel, __assign({}, htmlAttributes, { tabIndex: -1, "aria-modal": "true", role: "dialog", "aria-label": label, modalRef: modalRef, focusOn: initialFocus || modalRef, returnFocusTo: returnFocusTo, onDismiss: onDismiss, className: modalClassNames, style: __assign(__assign({}, zIndexStyle), minWidthStyle) }), children)));
    return (React.createElement(TransitionGroup, null, isOpen && (React.createElement(CSSTransition, { nodeRef: modalRef, classNames: transitionClassNames, timeout: 200, mountOnEnter: true, unmountOnExit: true }, isFullScreen ? (ModalContent) : (React.createElement("div", { className: modalOverlayClassNames, "data-testid": "modal-overlay", onClick: onOverlayClick, onKeyDown: onOverlayKeyDown, role: "none", style: zIndexStyle }, ModalContent))))));
};
Modal.propTypes = {
    isOpen: PropTypes.bool,
    closeOnOutsideClick: PropTypes.bool,
    className: PropTypes.string,
    label: PropTypes.string,
    overlayVariant: PropTypes.oneOf(['default', 'dark']),
    minWidth: PropTypes.string || PropTypes.number,
    radius: (_a = Panel === null || Panel === void 0 ? void 0 : Panel.propTypes) === null || _a === void 0 ? void 0 : _a.radius,
    children: PropTypes.node,
    zIndex: PropTypes.number,
    isFullScreen: PropTypes.bool,
    isRightAligned: PropTypes.bool,
};
//# sourceMappingURL=Modal.js.map